import { useState } from 'react';
import { UM } from '../_components';
import type { Building } from './types';

// ─── Collapsible Section ─────────────────────────────────────────────────────

function CollapsibleSection({ title, defaultOpen = false, children }: { title: string; defaultOpen?: boolean; children: React.ReactNode }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={{ marginBottom: 12 }}>
      <button
        onClick={() => setOpen(!open)}
        style={{
          width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '8px 0', border: 'none', background: 'none', cursor: 'pointer',
          fontSize: 13, fontWeight: 600, color: UM.text,
        }}
      >
        {title}
        <span style={{ fontSize: 12, color: UM.textMuted, transition: 'transform 0.2s', transform: open ? 'rotate(90deg)' : 'rotate(0deg)' }}>▶</span>
      </button>
      {open && <div style={{ paddingTop: 4 }}>{children}</div>}
    </div>
  );
}

// ─── Identification row ──────────────────────────────────────────────────────

function IdentRow({ label, value, link }: { label: string; value: string; link?: string }) {
  return (
    <tr>
      <td style={{ padding: '5px 8px', borderBottom: '1px solid #f1f5f9', color: '#6b7280', fontWeight: 500, width: '40%', fontSize: 12 }}>{label}</td>
      <td style={{ padding: '5px 8px', borderBottom: '1px solid #f1f5f9', color: UM.text, textAlign: 'right', fontSize: 12 }}>
        {link ? (
          <a href={link} target="_blank" rel="noopener noreferrer" style={{ color: '#2563eb', textDecoration: 'underline' }}>{value}</a>
        ) : (
          value
        )}
      </td>
    </tr>
  );
}

// ─── Mini Map placeholder ────────────────────────────────────────────────────

function MiniMapPlaceholder() {
  return (
    <div style={{
      width: '100%', height: 200, borderRadius: 8, marginBottom: 16,
      background: 'linear-gradient(135deg, #e0f2fe 0%, #bae6fd 50%, #7dd3fc 100%)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      border: `1px solid ${UM.border}`, position: 'relative', overflow: 'hidden',
    }}>
      {/* Building polygon placeholder */}
      <div style={{
        width: 80, height: 60, background: 'rgba(0, 157, 233, 0.3)',
        border: '2px solid #009de9', borderRadius: 4,
      }} />
      <span style={{ position: 'absolute', bottom: 8, right: 8, fontSize: 11, color: UM.textMuted, background: 'rgba(255,255,255,0.8)', padding: '2px 6px', borderRadius: 4 }}>
        Mini carte
      </span>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

interface BuildingIdPanelProps {
  building: Building;
}

export function BuildingIdPanel({ building }: BuildingIdPanelProps) {
  const { identification } = building;

  return (
    <div style={{
      width: 350, minWidth: 350, overflowY: 'auto', padding: 16,
      borderRight: `1px solid ${UM.border}`, background: UM.bg,
    }}>
      <MiniMapPlaceholder />

      {/* Identification section */}
      <CollapsibleSection title="Identification du bâtiment" defaultOpen>
        <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
          <tbody>
            <IdentRow label="ID UrbanMap" value={identification.urbanMapId} />
            <IdentRow label="ID RNB" value={identification.rnbIds.join(' – ')} link={`https://rnb.beta.gouv.fr/carte?q=${identification.rnbIds[0]}`} />
            <IdentRow label="ID BDNB" value={identification.bdndId} />
            <IdentRow label="ID IGN" value={identification.ignId} />
            <IdentRow label="Parcelle principale" value={identification.parcelleId} />
            <IdentRow label="ID Installation GENESIS" value={identification.installationGenesis.join(' – ')} />
            <IdentRow label="ID LPDI" value={identification.idPlaceImmobilier} link={`https://www.laplacedelimmobilier-pro.com/app/Buildings/${identification.idPlaceImmobilier}`} />
            <IdentRow label="ID Explore" value={identification.idExplore} link="#" />
          </tbody>
        </table>
      </CollapsibleSection>

      {/* Comments section */}
      <CollapsibleSection title="Commentaires" defaultOpen>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ padding: 10, background: '#f9fafb', borderRadius: 6, border: '1px solid #e5e7eb' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
              <span style={{ fontSize: 12, fontWeight: 600, color: UM.text }}>j.dupont</span>
              <span style={{ fontSize: 11, color: UM.textMuted }}>12/03/2025</span>
            </div>
            <p style={{ margin: 0, fontSize: 12, color: '#4b5563', lineHeight: 1.5 }}>
              Bâtiment raccordé au réseau en 2024. Compteur installé sous-station B2.
            </p>
          </div>
          <div style={{ padding: 10, background: '#f9fafb', borderRadius: 6, border: '1px solid #e5e7eb' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
              <span style={{ fontSize: 12, fontWeight: 600, color: UM.text }}>m.bernard</span>
              <span style={{ fontSize: 11, color: UM.textMuted }}>08/01/2025</span>
            </div>
            <p style={{ margin: 0, fontSize: 12, color: '#4b5563', lineHeight: 1.5 }}>
              Contact syndic : Foncia Brétigny (01 60 xx xx xx)
            </p>
          </div>
          {/* New comment input */}
          <div style={{ display: 'flex', gap: 8 }}>
            <input
              type="text"
              placeholder="Ajouter un commentaire..."
              style={{ flex: 1, padding: '8px 10px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 12 }}
            />
            <button style={{ padding: '8px 12px', borderRadius: 6, border: 'none', background: UM.accent, color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
              Envoyer
            </button>
          </div>
        </div>
      </CollapsibleSection>
    </div>
  );
}
